import {
  DEFAULT_LONG_FORMAT,
  getDateInUserLocale,
  getTimestampInMillisecondsFromSecond
} from "@/helpers/date.helpers"

import type {
  ActionWorkflowResponse,
  TestActionResponse,
  TriggerOptionsWorkflowResponse,
  WorkflowResponse
} from "@/api/useWorkflow.types"

import type { TestActionResult } from "@/organisms/Ticketing/components/JiraTemplateModal/JiraTemplateModal.types"
export const getWorkflow = (response: WorkflowResponse) => {
  const { data = [], metadata } = response || {}

  return {
    metadata,
    data: Array.isArray(data)
      ? data.map((workflow) => ({
          key: workflow.id,
          created: { email: workflow.createdBy },
          lastTriggeredDate: !workflow.lastTriggered
            ? ""
            : getDateInUserLocale(
                getTimestampInMillisecondsFromSecond(workflow.lastTriggered),
                DEFAULT_LONG_FORMAT
              ),
          ...workflow
        }))
      : [data]
  }
}

export const getTriggerOptionsWorkflow = (
  response: TriggerOptionsWorkflowResponse
) => {
  return response.data.reduce(
    (acc, { disabled, key, displayName, types }) => {
      acc.categories.push({ disabled, label: displayName, value: key })
      acc.types[key] = types?.map(({ key: value }) => value)

      return acc
    },
    { categories: [], types: {} } as {
      categories: { label: string; value: string; disabled: boolean }[]
      types: Record<string, string[]>
    }
  )
}

// A template test passes once the vendor returns the ticket it created
export const toTemplateTestResult = (
  response: TestActionResponse
): TestActionResult => {
  if (response.error) return { success: false, error: response.error }
  if (response.data && "ticketKey" in response.data)
    return {
      success: true,
      ticketKey: response.data.ticketKey,
      ticketUrl: response.data.ticketUrl
    }

  return { success: false }
}

// The extra of a vendor ticket action; null until the backend offers the action
export const getTicketIssueExtra = (response: ActionWorkflowResponse) => {
  const extra = response.data?.extra

  switch (extra?.type) {
    case "azure_boards_issue":
    case "asana_task":
    case "github_issue":
      return extra
    default:
      return null
  }
}

export const getActionsWorkflow = (response: ActionWorkflowResponse) => {
  const { data } = response

  if (!data?.extra) return []

  switch (data.extra.type) {
    case "webhook":
      return data.extra.webhooks.map(({ id, name }) => ({
        label: name,
        value: id
      }))
    case "label":
      return data.extra.labels.map(({ id, name, color }) => ({
        label: name,
        value: id,
        color: color
      }))
    case "jira_issue":
    case "slack_message":
    case "azure_boards_issue":
    case "asana_task":
    case "github_issue":
      return data.extra

    default:
      return []
  }
}
